import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, Button, useToast } from '@caruso/ui';
import { FileText, Search, Filter, ChevronDown, ChevronUp, ExternalLink, Clock, CheckCircle, AlertCircle } from 'lucide-react';
import { api } from '@/lib/axios';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface FormSubmission {
  id: string;
  token: string;
  status: string;
  formData: {
    title: string;
    description?: string;
    fields: Array<{
      id: string;
      label: string;
      type: string;
      variableName?: string;
    }>;
  };
  responseData: Record<string, any> | null;
  client: {
    id: string;
    name: string;
    email: string | null;
    phone: string;
  };
  execution: {
    id: string;
    flow: {
      id: string;
      name: string;
    };
  };
  createdAt: string;
  submittedAt: string | null;
  expiresAt: string | null;
}

interface FormSubmissionsResponse {
  data: FormSubmission[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export function FormSubmissionsPage() {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const { toast } = useToast();

  const { data, isLoading, error } = useQuery<FormSubmissionsResponse>({
    queryKey: ['form-submissions', page, statusFilter],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20',
      });
      if (statusFilter) params.append('status', statusFilter);

      const response = await api.get(`/forms/submissions/list?${params}`);
      return response.data;
    },
  });

  const toggleRow = (id: string) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, { className: string; icon: any; label: string }> = {
      pending: {
        className: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
        icon: Clock,
        label: 'Pendente',
      },
      submitted: {
        className: 'bg-green-500/10 text-green-400 border-green-500/20',
        icon: CheckCircle,
        label: 'Enviado',
      },
      expired: {
        className: 'bg-red-500/10 text-red-400 border-red-500/20',
        icon: AlertCircle,
        label: 'Expirado',
      },
    };

    const config = variants[status] || variants.pending;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium border ${config.className}`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </span>
    );
  };

  const renderFieldValue = (field: any, value: any) => {
    if (value === null || value === undefined) return '-';

    if (Array.isArray(value)) {
      return value.join(', ');
    }

    if (field.type === 'date') {
      try {
        return format(new Date(value), 'dd/MM/yyyy', { locale: ptBR });
      } catch {
        return value;
      }
    }

    if (field.type === 'time') {
      return value;
    }

    if (typeof value === 'boolean') {
      return value ? 'Sim' : 'Não';
    }

    return value.toString();
  };

  return (
    <div className="space-y-6 p-4 sm:p-6 lg:p-8">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-brand-ivory flex items-center gap-2">
            <FileText className="h-7 w-7 sm:h-8 sm:w-8 text-brand-gold" />
            Respostas de Formulários
          </h1>
          <p className="text-brand-ivory/60 mt-1">
            Visualize todas as respostas dos formulários enviados nos fluxos
          </p>
        </div>
      </div>

      {/* Filters */}
      <Card className="p-4 bg-brand-smoke border-brand-gold/20">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-brand-ivory/70 mb-2">
              Status
            </label>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
              className="w-full px-3 py-2 bg-brand-ink border border-brand-gold/20 rounded-md text-brand-ivory focus:outline-none focus:ring-2 focus:ring-brand-gold"
            >
              <option value="">Todos</option>
              <option value="pending">Pendente</option>
              <option value="submitted">Enviado</option>
              <option value="expired">Expirado</option>
            </select>
          </div>
        </div>
      </Card>

      {/* Results */}
      {isLoading ? (
        <Card className="p-8 bg-brand-smoke border-brand-gold/20">
          <p className="text-center text-brand-ivory/60">Carregando...</p>
        </Card>
      ) : error ? (
        <Card className="p-8 bg-brand-smoke border-brand-gold/20">
          <p className="text-center text-red-400">Erro ao carregar formulários</p>
        </Card>
      ) : !data?.data.length ? (
        <Card className="p-8 bg-brand-smoke border-brand-gold/20">
          <p className="text-center text-brand-ivory/60">Nenhum formulário encontrado</p>
        </Card>
      ) : (
        <>
          <div className="space-y-4">
            {data.data.map((submission) => {
              const isExpanded = expandedRows.has(submission.id);

              return (
                <Card
                  key={submission.id}
                  className="bg-brand-smoke border-brand-gold/20 overflow-hidden"
                >
                  <div className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="text-lg font-semibold text-brand-ivory truncate">
                            {submission.formData.title}
                          </h3>
                          {getStatusBadge(submission.status)}
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                          <div>
                            <span className="text-brand-ivory/60">Cliente:</span>{' '}
                            <span className="text-brand-ivory">{submission.client.name}</span>
                          </div>
                          <div>
                            <span className="text-brand-ivory/60">Fluxo:</span>{' '}
                            <span className="text-brand-ivory">{submission.execution.flow.name}</span>
                          </div>
                          <div>
                            <span className="text-brand-ivory/60">Criado em:</span>{' '}
                            <span className="text-brand-ivory">
                              {format(new Date(submission.createdAt), "dd/MM/yyyy 'às' HH:mm", {
                                locale: ptBR,
                              })}
                            </span>
                          </div>
                          {submission.submittedAt && (
                            <div>
                              <span className="text-brand-ivory/60">Enviado em:</span>{' '}
                              <span className="text-brand-ivory">
                                {format(new Date(submission.submittedAt), "dd/MM/yyyy 'às' HH:mm", {
                                  locale: ptBR,
                                })}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>

                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleRow(submission.id)}
                        className="flex-shrink-0"
                      >
                        {isExpanded ? (
                          <ChevronUp className="h-5 w-5" />
                        ) : (
                          <ChevronDown className="h-5 w-5" />
                        )}
                      </Button>
                    </div>

                    {isExpanded && submission.responseData && (
                      <div className="mt-4 pt-4 border-t border-brand-gold/20">
                        <h4 className="text-sm font-semibold text-brand-ivory mb-3">Respostas:</h4>
                        <div className="space-y-3">
                          {submission.formData.fields.map((field) => {
                            const value = submission.responseData![field.id];

                            return (
                              <div
                                key={field.id}
                                className="bg-brand-ink/50 rounded-lg p-3"
                              >
                                <div className="text-sm font-medium text-brand-ivory/70 mb-1">
                                  {field.label}
                                </div>
                                <div className="text-brand-ivory">
                                  {renderFieldValue(field, value)}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {isExpanded && !submission.responseData && (
                      <div className="mt-4 pt-4 border-t border-brand-gold/20">
                        <p className="text-center text-brand-ivory/60 text-sm">
                          Este formulário ainda não foi respondido
                        </p>
                      </div>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>

          {/* Pagination */}
          {data.pagination.totalPages > 1 && (
            <div className="flex items-center justify-center gap-2">
              <Button
                variant="outline"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                Anterior
              </Button>
              <span className="text-brand-ivory">
                Página {page} de {data.pagination.totalPages}
              </span>
              <Button
                variant="outline"
                onClick={() => setPage((p) => Math.min(data.pagination.totalPages, p + 1))}
                disabled={page === data.pagination.totalPages}
              >
                Próxima
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
